"use client";
import Image from "next/image";
import { Link } from "@/navigation";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { CloudArrowUp, LockSimple } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

const DEFAULT_CAROUSEL_IMAGES = [
	"/image/new-version/machining/cnc-milling/banner/online-custom-cnc-milling-services-01.png",
	"/image/new-version/machining/cnc-milling/banner/online-custom-cnc-milling-services-02.png",
	"/image/new-version/machining/cnc-milling/banner/online-custom-cnc-milling-services-03.png",
];

interface SlugConfig {
	namespace: string;
	images: string[];
}

/** 连续编号 bannerFeature1、bannerFeature2、… 直到 `t.has` 为 false；上限防止异常键名死循环 */
const MAX_BANNER_FEATURES = 20;

const SLUG_CONFIG: Record<string, SlugConfig> = {
	stamping: {
		namespace: "new-stamping",
		images: [
			"/image/new-version/fabrication/metal-stamping/banner/metal-stamping-1.webp",
			"/image/new-version/fabrication/metal-stamping/banner/metal-stamping-2.webp",
			"/image/new-version/fabrication/metal-stamping/banner/metal-stamping-3.webp",
		],
	},
	welding: {
		namespace: "new-welding",
		images: [
			"/image/new-version/fabrication/metal-welding/banner/metal-welding-10.webp",
			"/image/new-version/fabrication/metal-welding/banner/metal-welding-12.webp",
			"/image/new-version/fabrication/metal-welding/banner/metal-welding-15.webp",
		],
	},
	"metal-casting": {
		namespace: "new-metal-casting",
		images: [
			"/image/new-version/casting/Metal-Casting/banner/metal-casting-1.webp",
			"/image/new-version/casting/Metal-Casting/banner/metal-casting-2.webp",
			"/image/new-version/casting/Metal-Casting/banner/metal-casting-3.webp",
		],
	},
	"investment-casting": {
		namespace: "new-investment-casting",
		images: [
			"/image/new-version/casting/Investment-Casting/banner/investment-casting-1.webp",
			"/image/new-version/casting/Investment-Casting/banner/investment-casting-2.webp",
			"/image/new-version/casting/Investment-Casting/banner/investment-casting-3.webp",
		],
	},
	"high-pressure-casting": {
		namespace: "new-high-pressure-casting",
		images: [
			"/image/new-version/casting/High-Pressure-Die-Casting/banner/high-pressure-die-casting-parts-1.webp",
			"/image/new-version/casting/High-Pressure-Die-Casting/banner/high-pressure-die-casting-parts-2.webp",
			"/image/new-version/casting/High-Pressure-Die-Casting/banner/high-pressure-die-casting-parts-3.webp",
		],
	},
	"low-pressure-casting": {
		namespace: "new-low-pressure-casting",
		images: [
			"/image/new-version/casting/Low-Pressure-Die-Casting/banner/low-pressure-die-casting-parts-1.webp",
			"/image/new-version/casting/Low-Pressure-Die-Casting/banner/low-pressure-die-casting-parts-2.webp",
			"/image/new-version/casting/Low-Pressure-Die-Casting/banner/low-pressure-die-casting-parts-3.webp",
		],
	},
	"sand-casting": {
		namespace: "new-sand-casting",
		images: [
			"/image/new-version/casting/Sand-Casting/banner/sand-casting-1.webp",
			"/image/new-version/casting/Sand-Casting/banner/sand-casting-2.webp",
			"/image/new-version/casting/Sand-Casting/banner/sand-casting-3.webp",
		],
	},
	"die-casting": {
		namespace: "new-die-casting",
		images: [
			"/image/new-version/casting/Die-Casting/banner/die-casting-1.webp",
			"/image/new-version/casting/Die-Casting/banner/die-casting-2.webp",
			"/image/new-version/casting/Die-Casting/banner/die-casting-3.webp",
		],
	},
	"vacuum-casting": {
		namespace: "new-vacuum-casting",
		images: [
			"/image/new-version/casting/Vacuum-Casting/banner/vacuum-casting-1.webp",
			"/image/new-version/casting/Vacuum-Casting/banner/vacuum-casting-2.webp",
			"/image/new-version/casting/Vacuum-Casting/banner/vacuum-casting-3.webp",
		],
	},
	"3d-printing": {
		namespace: "new-3d-printing",
		images: [
			"/image/new-version/more/3D-Printing/banner/3d-printing-parts-1.webp",
			"/image/new-version/more/3D-Printing/banner/3d-printing-parts-2.webp",
			"/image/new-version/more/3D-Printing/banner/3d-printing-parts-3.webp",
		],
	},
	"aluminum-extrusion": {
		namespace: "new-aluminum-extrusion",
		images: [
			"/image/new-version/more/AluminumExtrusion/横幅/aluminum-extrusion-parts-1.webp",
			"/image/new-version/more/AluminumExtrusion/横幅/aluminum-extrusion-parts-2.webp",
			"/image/new-version/more/AluminumExtrusion/横幅/aluminum-extrusion-parts-3.webp",
		],
	},
	"spring-manufacturing": {
		namespace: "new-spring-manufacturing",
		images: [
			"/image/new-version/more/SpringManufacturing/banner/spring-manufacturing-parts-1.webp",
			"/image/new-version/more/SpringManufacturing/banner/spring-manufacturing-parts-2.webp",
			"/image/new-version/more/SpringManufacturing/banner/spring-manufacturing-parts-3.webp",
		],
	},
	"surface-finishing": {
		namespace: "new-surface-finishing",
		images: [
			"/image/new-version/ValueAddedSolutions/surface-finishing/横幅/surface-finishing-parts-1.webp",
			"/image/new-version/ValueAddedSolutions/surface-finishing/横幅/surface-finishing-parts-2.webp",
			"/image/new-version/ValueAddedSolutions/surface-finishing/横幅/surface-finishing-parts-13.webp",
		],
	},
	"on-demand-manufacturing": {
		namespace: "new-on-demand-manufacturing",
		images: [
			"/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing/banner/precision-cnc-machining-parts.webp",
			"/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing/banner/cncmachining-parts.webp",
			"/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing/banner/precision-sheet-metal-bending-parts.webp",
		],
	},
	assembly: {
		namespace: "new-assembly",
		images: [
			"/image/new-version/ValueAddedSolutions/assembly/banner/assembly-1.webp",
			"/image/new-version/ValueAddedSolutions/assembly/banner/assembly-2.webp",
			"/image/new-version/ValueAddedSolutions/assembly/banner/assembly-3.webp",
		],
	},
	"industrial-automation": {
		namespace: "new-industrial-automation",
		images: [
			"/image/new-version/ValueAddedSolutions/industrial-automation/横幅/cnc-machining-parts-3.webp",
			"/image/new-version/ValueAddedSolutions/industrial-automation/横幅/cnc-machining-parts-5.webp",
			"/image/new-version/ValueAddedSolutions/industrial-automation/横幅/cnc-machining-parts-6.webp",
		],
	},
	"sla-stereolithography": {
		namespace: "new-sla-stereolithography",
		images: [
			"/image/new-version/more/SLA/banner/sla-parts-1.webp",
			"/image/new-version/more/SLA/banner/sla-parts-2.webp",
			"/image/new-version/more/SLA/banner/sla-parts-3.webp",
		],
	},
	"sls-selective-laser-sintering": {
		namespace: "new-sls-selective-laser-sintering",
		images: [
			"/image/new-version/more/SLS/banner/sls-parts-1.webp",
			"/image/new-version/more/SLS/banner/sls-parts-2.webp",
			"/image/new-version/more/SLS/banner/sls-parts-3.webp",
		],
	},
	"fdm-fused-deposition-modeling": {
		namespace: "new-fdm-fused-deposition-modeling",
		images: [
			"/image/new-version/more/FDM/banner/fdm-parts-1.webp",
			"/image/new-version/more/FDM/banner/fdm-parts-2.webp",
			"/image/new-version/more/FDM/banner/fdm-parts-3.webp",
		],
	},
	"mjf-multi-jet-fusion": {
		namespace: "new-mjf-multi-jet-fusion",
		images: [
			"/image/new-version/more/MJF/banner/mjf-parts-1.webp",
			"/image/new-version/more/MJF/banner/mjf-parts-2.webp",
			"/image/new-version/more/MJF/banner/mjf-parts-3.webp",
		],
	},
};

// ─── 内部子组件 ───

const UploadIcon = ({ className }: { className?: string }) => (
	<svg viewBox="12 14 40 32" className={className}>
		<path
			fill="#0F7E03"
			d="M15,44V15c0-0.55,0.45-1,1-1h7.73c0.25,0,0.5,0.1,0.68,0.27l4.26,3.99c0.19,0.17,0.43,0.27,0.68,0.27H48c0.55,0,1,0.45,1,1V44c0,0.55-0.45,1-1,1H16C15.45,45,15,44.55,15,44z"
		/>
		<path
			fill="#65A525"
			d="M12.18,27.16c-0.1-0.61,0.37-1.16,0.99-1.16h37.66c0.61,0,1.08,0.55,0.99,1.16l-2.68,17c-0.08,0.49-0.5,0.84-0.99,0.84H15.85c-0.49,0-0.91-0.36-0.99-0.84L12.18,27.16z"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg
		className="h-5 w-5 flex-shrink-0 text-themePrimary400"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M4 10 Q 7 14 9 12 Q 13 8 16 5" />
	</svg>
);

// ─── 主组件 ───

const ServiceBanner = ({ slug }: { slug: string }) => {
	const config = SLUG_CONFIG[slug];
	const ns = config?.namespace;
	const carouselImages = config?.images ?? DEFAULT_CAROUSEL_IMAGES;
	const t = useTranslations();
	const { openModalInquiry } = useModalInquiryContext();

	const b = (key: string, fallbackNs: string, fallbackKey: string) =>
		ns ? t(`${ns}.${key}`) : t(`${fallbackNs}.${fallbackKey}`);

	const collectNumberedMessages = (messageBase: string, keyPrefix: string): string[] => {
		const out: string[] = [];
		for (let i = 1; i <= MAX_BANNER_FEATURES; i++) {
			const key = `${messageBase}.${keyPrefix}${i}`;
			if (!t.has(key)) break;
			out.push(t(key));
		}
		return out;
	};

	const features = ns
		? collectNumberedMessages(ns, "bannerFeature")
		: collectNumberedMessages("cncMillingBanner", "feature");

	return (
		<section className="relative min-h-[400px] w-full overflow-hidden sm:min-h-[450px] md:min-h-[500px]">
			<div className="absolute inset-0 bg-[linear-gradient(180deg,_#ffffff_0%,_#ffffff_75%,_#eef6e5_90%,_#ecfed4_100%)]" />

			<div className="container relative z-10 px-4 pb-10 pt-8 font-sans sm:px-6 sm:pb-14 sm:pt-12 md:pb-16 md:pt-14 lg:pb-20 lg:pt-16">
				<div className="flex h-full w-full flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
					<div className="max-w-[600px] xl:min-w-[600px]">
						<div className="-ml-[18px] inline-block">
							<div
								className="trustpilot-widget scale-125"
								data-locale="en-US"
								data-template-id="5419b637fa0340045cd0c936"
								data-businessunit-id="68808142dd0a4c4d3e411b9c"
								data-style-height="24px"
								data-style-width="100%"
							>
								<a
									href="https://www.trustpilot.com/review/www.cncprotolabs.com?utm_medium=trustbox&utm_source=MicroTrustScore"
									target="_blank"
									rel="noopener"
								>
									trustpilotRating
								</a>
							</div>
						</div>
						<h1 className="mb-4 text-xl font-bold leading-tight text-black sm:mb-6 sm:text-[24px] md:mb-8 md:text-[30px] lg:text-[36px] xl:text-[44px]">
							{b("bannerTitle", "cncMillingBanner", "mainTitle")}
						</h1>

						<p className="mb-4 text-sm leading-relaxed text-black sm:mb-6 md:mb-8 md:text-base">
							{b("bannerDescription", "cncMillingBanner", "description")}
						</p>

						<ul className="mb-8 space-y-2 sm:mb-10 sm:space-y-2.5 md:mb-12 md:space-y-3">
							{features.map((feature, index) => {
								const separatorIdx = feature.search(/[：:]/);
								const hasSeparator = separatorIdx !== -1;
								return (
									<li key={index} className="flex items-start gap-2">
										<CheckIcon />
										<span className="text-xs leading-relaxed text-black sm:text-sm md:text-base">
											{hasSeparator ? (
												<>
													<strong>{feature.slice(0, separatorIdx + 1)}</strong>
													{feature.slice(separatorIdx + 1)}
												</>
											) : (
												feature
											)}
										</span>
									</li>
								);
							})}
						</ul>

						<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
							<Link
								href="/upload"
								className="group inline-flex w-full items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
							>
								<CloudArrowUp className="h-4 w-4 shrink-0 md:h-[18px] md:w-[18px]" weight="fill" />
								{b("bannerGetInstantQuote", "cncMillingBanner", "primaryButtonText")}
							</Link>
							<button
								type="button"
								onClick={openModalInquiry}
								className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
							>
								{b("bannerStartProductionQuote", "cncMillingBanner", "secondaryButtonText")}
							</button>
						</div>
					</div>

					<div className="relative w-full flex-1 lg:max-w-[600px]">
						<div className="mx-auto max-w-[600px] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] md:p-8">
							{carouselImages.length > 0 && (
								<div className="mb-3 flex items-center justify-center gap-4">
									{carouselImages.map((src, index) => (
										<div key={index} className="relative aspect-square w-[100px] md:w-[120px]">
											<Image
												src={src}
												alt={`Part ${index + 1}`}
												fill
												className="object-contain"
												sizes="120px"
											/>
										</div>
									))}
								</div>
							)}

							<p className="mb-4 text-center text-sm text-[#39576f] md:text-lg">
								{b("bannerUploadSubtitle", "serviceBanner", "uploadTitle")}
							</p>

							<Link href="/upload" className="block">
								<div className="rounded-lg border-2 border-dashed border-[#8abd5a] bg-[#f8fff7] p-5 transition-all duration-300 hover:border-mainColor hover:bg-[#f8fff7] md:p-6">
									<p className="mb-3 text-center text-sm text-black md:text-base">
										{b("bannerUploadAreaTitle", "serviceBanner", "uploadSubtitle")}
									</p>
									<p className="mb-4 text-center text-[10px] text-gray-900">
										{b("bannerUploadFormats", "serviceBanner", "uploadFormats")}
									</p>
									<div className="mb-3 flex justify-center">
										<UploadIcon className="h-12 w-12" />
									</div>
									<p className="mb-2 text-center text-xs text-gray-900">
										{ns
											? `${t(`${ns}.bannerFileSize`)}: ${t(`${ns}.bannerFileSizeLimit`)}`
											: t("serviceBanner.fileSize")}
									</p>
									<div className="flex items-center justify-center gap-1 text-xs text-gray-400">
										<LockSimple className="h-3 w-3" weight="fill" />
										<span>{b("bannerUploadSecure", "serviceBanner", "uploadSecure")}</span>
									</div>
								</div>
							</Link>

							<p className="mt-5 text-center text-xs text-gray-900 md:text-xs">
								{b("bannerTrustedBy", "serviceBanner", "trustedBy")}
							</p>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default ServiceBanner;
